import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../api/axios";
import { API_BASE_URL } from "../api/config";
import Logo from "../assets/images/Logo_HIRSCH.png";
import { CONSENT_EVENT, getCookieConsent } from "../components/CookieBanner";
import { GOOGLE_ADS_CONVERSION_LABEL, GOOGLE_ADS_ID, loadGoogleTag } from "../components/GoogleTag";

export default function CheckoutSuccess() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [consent, setConsent] = useState(getCookieConsent);
    const conversionStarted = useRef(false);

    // read parameter from URL CHECKOUT_SESSION_ID
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutSessionId = urlParams.get("session_id");

    useEffect(() => {
        const updateConsent = () => setConsent(getCookieConsent());
        window.addEventListener(CONSENT_EVENT, updateConsent);
        return () => window.removeEventListener(CONSENT_EVENT, updateConsent);
    }, []);

    useEffect(() => {
        const clearBookingStorage = (storage) => {
            const keysToRemove = [];
            for (let i = 0; i < storage.length; i += 1) {
                const key = storage.key(i);
                if (key && key.startsWith("booking_")) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach((key) => storage.removeItem(key));
        };

        clearBookingStorage(sessionStorage);
        clearBookingStorage(localStorage);

        axios.delete("/cart/clear").catch((error) => {
            console.error("Cart konnte nach Checkout nicht geleert werden:", error);
        });
    }, []);

    useEffect(() => {
        const trackConversion = async () => {
            console.log("Tracking conversion for checkout session ID:", checkoutSessionId);
            if (!checkoutSessionId || !consent.marketing || !GOOGLE_ADS_ID || !GOOGLE_ADS_CONVERSION_LABEL || conversionStarted.current) {
                console.log("Conversion tracking skipped due to missing information or consent.");
                return;
            }
            conversionStarted.current = true;

            loadGoogleTag(consent);
            console.log("Google Tag loaded with consent:", consent);
            const response = await fetch(`${API_BASE_URL}/cart/conversion?checkoutSessionId=${encodeURIComponent(checkoutSessionId)}`, {
                credentials: "include"
            });
            if (!response.ok) throw new Error("Conversion-Daten konnten nicht geladen werden");

            const data = await response.json();

            if (!data || data.tracked) return;
            console.log("sending gtag conversion event with data:", data);
            window.gtag("event", "conversion", {
                send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`,
                transaction_id: checkoutSessionId,
                value: data.value,
                currency: data.currency,
            });
            console.log("gtag conversion event sent successfully.");
            const updateResponse = await fetch(`${API_BASE_URL}/cart/conversion?checkoutSessionId=${encodeURIComponent(checkoutSessionId)}`, {
                method: "PUT",
                credentials: "include"
            });
            if (!updateResponse.ok) throw new Error("Conversion konnte nicht als verarbeitet gespeichert werden");
        };
        trackConversion().catch((error) => console.error("Conversion konnte nicht erfasst werden:", error));
    }, [checkoutSessionId, consent]);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <header className="bg-white shadow-sm sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-32">
                        <div className="flex items-center">
                            <Link to="/" className="flex items-center">
                                <img src={Logo} alt="Hirsch Logo" className="h-12 lg:h-24" />
                            </Link>
                        </div>

                        <nav className="hidden lg:flex items-center space-x-8">
                            <a href="/#hero" className="text-gray-600 hover:text-gray-900 font-medium text-xl">
                                Jetzt buchen!
                            </a>
                            <a href="/#abfallarten" className="text-gray-600 hover:text-gray-900 font-medium text-xl">
                                Abfallarten
                            </a>
                            <a href="/#container" className="text-gray-600 hover:text-gray-900 font-medium text-xl">
                                Container
                            </a>
                            <a href="/#faq" className="text-gray-600 hover:text-gray-900 font-medium text-xl">
                                Kontakt
                            </a>
                            <Link
                                to="/cart"
                                className="bg-pink-600 text-white px-6 py-2.5 rounded-md hover:bg-pink-700 transition-colors font-bold text-sm uppercase"
                            >
                                Warenkorb
                            </Link>
                        </nav>

                        <button
                            onClick={() => setIsMenuOpen((prev) => !prev)}
                            className="lg:hidden text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-all"
                            aria-label="Toggle menu"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </div>

                    {isMenuOpen && (
                        <div className="lg:hidden py-4 border-t border-gray-100">
                            <nav className="flex flex-col space-y-2">
                                <a href="/#hero" onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium py-3 px-3 rounded-lg transition-all">
                                    Jetzt buchen!
                                </a>
                                <a href="/#abfallarten" onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium py-3 px-3 rounded-lg transition-all">
                                    Abfallarten
                                </a>
                                <a href="/#container" onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium py-3 px-3 rounded-lg transition-all">
                                    Container
                                </a>
                                <a href="/#faq" onClick={() => setIsMenuOpen(false)} className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium py-3 px-3 rounded-lg transition-all">
                                    Kontakt
                                </a>
                                <Link to="/cart" className="bg-pink-600 text-white px-4 py-3 rounded-md hover:bg-pink-700 transition-colors font-bold inline-block text-center mt-2 uppercase">
                                    Warenkorb
                                </Link>
                            </nav>
                        </div>
                    )}
                </div>
            </header>

            <main className="flex-1 container mx-auto px-4 py-16">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-lg border border-gray-100 text-center">
                        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
                            <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>

                        <h1 className="text-3xl lg:text-4xl font-bold text-pink-600 mb-3">
                            Vielen Dank für Ihre Bestellung
                        </h1>
                        <p className="text-base sm:text-lg text-gray-700 mb-6">
                            Ihre Zahlung war erfolgreich und Ihre Bestellung wurde an uns übermittelt.
                        </p>

                        <div className="rounded-xl border border-pink-100 bg-pink-50 p-5 text-left">
                            <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
                                Sie erhalten in Kürze eine Bestätigungs-E-Mail. Falls die Nachricht nicht direkt im
                                Posteingang erscheint, prüfen Sie bitte auch Ihren Spam-Ordner. Sollte innerhalb von
                                24 Stunden keine E-Mail bei Ihnen eingehen, kontaktieren Sie uns bitte unter
                                <a href="mailto:container@hirsch-gmbh.com" className="ml-1 font-semibold text-pink-700 hover:underline">
                                    container@hirsch-gmbh.com
                                </a>.
                            </p>
                        </div>

                        <div className="mt-8">
                            <Link
                                to="/"
                                state={{ resetBooking: true }}
                                replace
                                className="inline-block bg-pink-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-pink-700 transition duration-300"
                            >
                                Zur Startseite
                            </Link>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer id="footer" className="bg-gray-100 text-gray-700 relative" >
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 max-w-6xl mx-auto">
                        {/* UNTERNEHMEN */}
                        <div>
                            <h3 className="text-sm font-bold mb-4 text-gray-500 uppercase tracking-wider">UNTERNEHMEN</h3>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <a href="https://hirsch-group.de/barrierefreiheitserklaerung"><li><span className="hover:text-pink-600 transition-colors cursor-pointer">Barrierefreiheitserklärung</span></li></a>
                                <a href="https://hirsch-group.de/widerrufsbelehrung"><li><span className="hover:text-pink-600 transition-colors cursor-pointer">Widerrufsbelehrung</span></li></a>
                                <a href="https://hirsch-group.de/datenschutz"><li><span className="hover:text-pink-600 transition-colors cursor-pointer">Datenschutz</span></li></a>
                                <a href="https://hirsch-group.de/kontakt"><li><span className="hover:text-pink-600 transition-colors cursor-pointer">Kontakt</span></li></a>
                                <a href="https://hirsch-group.de/impressum"><li><span className="hover:text-pink-600 transition-colors cursor-pointer">Impressum</span></li></a>
                                <a href="https://hirsch-group.de/agb"><li><span className="hover:text-pink-600 transition-colors cursor-pointer">AGB</span></li></a>
                            </ul>
                        </div>

                        {/* ÖFFNUNGSZEITEN */}
                        <div>
                            <h3 className="text-sm font-bold mb-4 text-gray-500 uppercase tracking-wider">ÖFFNUNGSZEITEN</h3>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li className="flex justify-between">
                                    <span>Montag</span>
                                    <span>07:00 - 15:30 Uhr</span>
                                </li>
                                <li className="flex justify-between">
                                    <span>Dienstag</span>
                                    <span>07:00 - 15:30 Uhr</span>
                                </li>
                                <li className="flex justify-between">
                                    <span>Mittwoch</span>
                                    <span>07:00 - 15:30 Uhr</span>
                                </li>
                                <li className="flex justify-between">
                                    <span>Donnerstag</span>
                                    <span>07:00 - 15:30 Uhr</span>
                                </li>
                                <li className="flex justify-between">
                                    <span>Freitag</span>
                                    <span>07:00 - 15:30 Uhr</span>
                                </li>
                                <li className="flex justify-between font-semibold">
                                    <span>Samstag</span>
                                    <span>GESCHLOSSEN</span>
                                </li>
                                <li className="flex justify-between font-semibold">
                                    <span>Sonntag</span>
                                    <span>GESCHLOSSEN</span>
                                </li>
                            </ul>
                        </div>

                        {/* KONTAKT */}
                        <div>
                            <div className="mb-6">
                                <img src={Logo} alt="Hirsch Logo" className="h-12 mb-6" />
                            </div>
                            <div className="space-y-3 text-sm text-gray-600">
                                <div className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                    </svg>
                                    <span>+49 (0) 421 - 25802 - 580</span>
                                </div>
                                <div className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                    </svg>
                                    <span>container@hirsch-gmbh.com</span>
                                </div>
                                <div className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                    </svg>
                                    <span>Hermann-Funk-Straße 6-9, 28309 Bremen</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bremen Skyline Section */}
                <div className="relative" >


                    {/* Pink Background Section with Copyright */}
                    <div className="bg-pink-600 text-white py-4">
                        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
                            <p className="text-sm">© All rights reserved</p>

                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
